"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { FaXmark } from "react-icons/fa6";
import AdvertisePopup from "./AdvertisePopup";
import { injectAd, incrementNavigationCounter } from "@/lib/gpt-loader";
import { useWebsiteSetting } from "./AdDataProvider";
import { useDevice } from '@/contexts/DeviceContext';

interface PageAdRendererProps {
  desktopAdCode: string | null;
  mobileAdCode: string | null;
  position: "sticky" | "lhs" | "rhs" | "inline";
  className?: string;
}

// Placeholder sizes to prevent CLS - reduced heights to match actual ad sizes
const AD_PLACEHOLDER_SIZES: Record<string, { minWidth: number; minHeight: number }> = {
  sticky: { minWidth: 320, minHeight: 50 },
  lhs: { minWidth: 160, minHeight: 250 },
  rhs: { minWidth: 300, minHeight: 250 },
  inline: { minWidth: 728, minHeight: 90 },
};

export default function PageAdRenderer({
  desktopAdCode,
  mobileAdCode,
  position,
  className = "",
}: PageAdRendererProps) {
  const adContentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasInjectedRef = useRef(false);
  const [isClosed, setIsClosed] = useState(false);
  const { isMobile, deviceDetected } = useDevice();
  const [isVisible, setIsVisible] = useState(false);
  const pathname = usePathname();

  // Get placeholder size for this position
  const placeholderSize = AD_PLACEHOLDER_SIZES[position] || { minWidth: 160, minHeight: 100 };

  // Reset injection flag when pathname changes (for client-side navigation)
  useEffect(() => {
    hasInjectedRef.current = false;
    incrementNavigationCounter(pathname);
  }, [pathname]);


  // Lazy loading with Intersection Observer
  useEffect(() => {
    if (isVisible || !containerRef.current) return;

    // Check if already in viewport
    const rect = containerRef.current.getBoundingClientRect();
    if (rect.top < window.innerHeight + 500) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: "200px",
        threshold: 0,
      }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [isVisible]);

  // Get the appropriate ad code based on device
  const adCode = isMobile ? (mobileAdCode || desktopAdCode) : desktopAdCode;

  // Check if sticky ad should be hidden via website setting
  const hideStickyAdSetting = useWebsiteSetting('hide-sticky-ad-slot');
  const hideStickyAd = hideStickyAdSetting?.value === true || hideStickyAdSetting?.value === 'true';

  // Handle ad code injection - wait for device detection, then only when visible
  useEffect(() => {
    if (!deviceDetected || hasInjectedRef.current || !isVisible) {
      return;
    }

    // Don't inject sticky ad if hidden by setting
    if (position === "sticky" && hideStickyAd) {
      return;
    }

    if (!adCode || !adContentRef.current) {
      return;
    }

    hasInjectedRef.current = true;

    // Use the shared GPT loader to inject the ad
    injectAd(adContentRef.current, adCode, `PageAdRenderer-${position}`);
  }, [adCode, position, isVisible, pathname, hideStickyAd, deviceDetected]);

  // Don't render if no ad code or closed
  if (!adCode || isClosed) {
    return null;
  }

  // Hide sticky ad if website setting says so
  if (position === "sticky" && hideStickyAd) {
    return null;
  }

  const CloseButton = () => (
    <button
      onClick={() => setIsClosed(true)}
      className="absolute top-0 right-0 w-8 h-8 flex items-center justify-center text-grey rounded-full z-[9999] transition-colors cursor-pointer"
      aria-label="Close ad"
    >
      <FaXmark size={14} />
    </button>
  );

  // Ad wrapper with styling
  const AdWrapper = ({ children }: { children: React.ReactNode }) => (
    <div className="m-[5px]">
      <div className="bg-gray-100 p-[5px] relative">
        {children}

        {/* Advertise with us - below ad (opens popup on click) */}
        <AdvertisePopup />
      </div>
    </div>
  );

  // Sticky position - fixed at bottom
  if (position === "sticky") {
    return (
      <div
        className={`w-full flex justify-center fixed bottom-0 left-0 right-0 z-50 shadow-lg ${className}`}
        ref={containerRef}
      >
        <div className="relative inline-block">
          <CloseButton />
          <AdWrapper>
            <div
              ref={adContentRef}
              style={{
                maxWidth: '100%',
                minHeight: placeholderSize.minHeight,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center' as const,
              }}
            />
          </AdWrapper>
        </div>
      </div>
    );
  }

  // LHS and RHS positions - simple container (sticky handled by parent)
  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <AdWrapper>
        <div
          ref={adContentRef}
          style={{
            maxWidth: '100%',
            minHeight: placeholderSize.minHeight,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center' as const,
          }}
        />
      </AdWrapper>
    </div>
  );
}
